// Catalog.ID Client — The *Wallets* group (Settings) and its shared rendering
// ============================================================================
// The drawing half of `bitcash-wallets-core.ts` (registration-wallets §7):
// Settings' own *Wallets* group, the wallet card both surfaces render, and the
// live Worker adapter behind them. Nothing here decides anything — every rule
// lives next door, where a test without a DOM can reach it.
//
// ── why this group no longer asks for the supersecret ───────────────────────
// It used to: a wallet key was bag-only and NEVER device-stored (D5), so a
// PIN-unlocked session held nothing about a wallet at all — not the key, not
// the label, not even how many there were — and the group opened with a
// supersecret prompt. On 2026-09-17 D5 was relaxed (cid-encrypted-storage §7):
// a personal wallet key is PIN-level like every other device-storable secret,
// and the list moved to the account's encrypted blob store under a key derived
// from its `derive_root`. The group now renders straight from
// `state/bitcash-wallets-store.ts` on any unlocked session, on ANY device the
// member has unlocked, and add/rename/revoke are compare-and-set writes to that
// document rather than key-bag upserts.
//
// What went with the prompt: *Lock wallets* (there is no derived key to zero),
// the "never on this device" copy, and the other-device cost — a bag write
// forced every OTHER enrolled device into a supersecret unlock at its next
// login (§3.4), and a blob write costs them nothing.
//
// ── which session sees what ─────────────────────────────────────────────────
// Settings is session-scoped, so this group serves a PERSONAL account over its
// own bag and an ORGANIZATION account over the organization's bag with no
// branch at all — `GET /api/account/keybag`, `POST /api/claims` and
// `POST /api/claims/{id}/publish` all resolve against whoever is signed in.
// That is also the only place the organization's receive address can be
// PUBLISHED: publication is `session.AccountId`-scoped server-side, so a
// delegate cannot publish a claim the organization owns (see
// `org-wallets-pane.ts`, which says so where a member would look for it).

import { createElement, createFieldGroup } from '@catalog-shell';
import type { PaneTypeRenderContext, ShellAPI } from '@catalog-shell';
import * as accountApi from '../api/account';
import * as claimsApi from '../api/claims';
import * as publicClaimsApi from '../api/public-claims';
import { formatApiError } from '../api/client';
import * as aes from '../crypto/aes';
import { encryptClaim, reEncryptClaimKey, reEncryptClaimKeyWithChain } from '../crypto/claims';
import { displayWalletKey } from '../crypto/bitcash';
import {
    planNewWallet, planWalletImport, walletSlotsFromDocument,
    type BitcashWalletsDocument,
} from '../crypto/bitcash-wallets-doc';
import {
    loadWallets, addWallet, renameWallet, revokeWallet,
} from '../state/bitcash-wallets-store';
import { keys as keyState } from '../state/keys';
import { bitcashWorker, ledgerContext } from '../state/bitcash-context';
import { fetchRegistrationConfig, type DefaultTokenTerms } from '../auth/payment-flow';
import { createBitcashWorkerClient } from '../key-grant/bitcash-worker-client';
import { newCode, viewCodeOf, walletRefOf, TYPE_FUND } from '../crypto/bitcash';
import { instrumentsOf, emptyInstrumentsDocument } from '../crypto/bitcash-instruments-doc';
import { loadInstruments, recordFund } from '../state/bitcash-instruments-store';
import { verifiedBalance } from '../api/ledger-rows';
import { BitcashWorkerError } from '../api/bitcash-worker';
import {
    getDefaultTokenCache, defaultTokenSlot, type DefaultTokenCache,
} from '../key-grant/bitcash-token-cache';
import {
    DEFAULT_TOKEN_LABEL, canUseOrgWallet, delegateTokenLabel, ensureReceiveAddress,
    findDefaultToken, findReceiveAddressPublication, isLiveToken, loadWalletView,
    mintDelegateToken, publishReceiveAddressClaim, reissueDefaultToken, requireCaps,
    revokeWalletToken, walletDisplayLabel, walletsUnavailableMessage, tokenOwnerText, defaultPermissionPrompt, tokenPayeeText,
    type TokenCaps, type WalletSlot, type WalletToken, type WalletView,
    type WalletsWorker, type WalletSurface,
} from './bitcash-wallets-core';

export { canUseOrgWallet };

// ── the live Worker ─────────────────────────────────────────────────────────

/**
 * The v2 ledger behind the panes' interface (apps/bitcash/docs/plans/bitcash-v2.md M12):
 *
 *   * tokens and the wallet ref through `key-grant/bitcash-worker-client.ts`,
 *     the one token service (codes and labels from the instruments blob);
 *   * existence by ref (`wallet/exists`), the balance with the VIEW code,
 *     confirmed by the latest signed row (`api/ledger-rows.ts`, D59);
 *   * receive addresses: the ledger lists refs, the codes come from the
 *     instruments blob, and a new one is generated here and written down BEFORE
 *     it is bound (T2).
 */
export function liveWalletsWorker(): WalletsWorker {
    const tokens = createBitcashWorkerClient();
    const ledger = bitcashWorker();
    const network = async () => {
        const ctx = await ledgerContext();
        if (ctx === null) throw new BitcashWorkerError(0, 'no_ledger', 'This Catalog.ID server is not connected to a Bitcash ledger.');
        return ctx.networkId;
    };
    return {
        listAuthTokens: (walletKey) => tokens.listAuthTokens(walletKey),
        createAuthToken: (input) => tokens.createAuthToken(input),
        revokeAuthToken: (walletKey, authToken) => tokens.revokeAuthToken(walletKey, authToken),
        getWalletRef: (walletKey) => tokens.getWalletRef(walletKey),
        exists: async (walletKey) => (await ledger.walletExists(walletRefOf(walletKey, await network()))).exists,
        balance: async (walletKey) => {
            const net = await network();
            return await verifiedBalance(ledger, walletKey, net, walletRefOf(walletKey, net));
        },
        createReceiveAddress: async (walletKey) => {
            const net = await network();
            const fund = newCode(TYPE_FUND);
            await recordFund(walletRefOf(walletKey, net), fund);   // written down first
            await ledger.createReceiveAddress(walletKey, fund);
            return { receiveAddr: fund };
        },
        listReceiveAddresses: async (walletKey) => {
            const net = await network();
            const listed = await ledger.walletAddresses(viewCodeOf(walletKey, net));
            const kept = instrumentsOf((await loadInstruments())?.doc ?? emptyInstrumentsDocument(), walletRefOf(walletKey, net)).funds;
            const byRef = new Map(kept.map(f => [walletRefOf(f.code, net), f.code]));
            return listed.addresses.filter(a => a.state === 'active').map(a => byRef.get(a.ref)).filter((c): c is string => c !== undefined);
        },
        listAddressRefs: async (walletKey) => {
            const net = await network();
            return (await ledger.walletAddresses(viewCodeOf(walletKey, net))).addresses.filter(a => a.state === 'active').map(a => a.ref);
        },
    };
}

/** The default token's terms — D27's caps and the service types (§9.3 D-a) — from the node's
 *  runtime config; null when the node names none (go-live configuration, M12 D15). */
async function tokenCaps(): Promise<DefaultTokenTerms | null> {
    return (await fetchRegistrationConfig()).defaultToken;
}

/** What a member reads when the node names no default-token terms. */
const NO_CAPS_MESSAGE = 'This Catalog.ID server names no caps and service types for the default permission yet, so none can be issued.';

// ── the Settings group ──────────────────────────────────────────────────────

/** What one render of the group is drawing. */
interface LoadedWallets {
    slots: WalletSlot[];
    /** The whole document, so `nextSlot` counts REVOKED wallets too. */
    doc: BitcashWalletsDocument;
}

export function appendWalletsGroup(
    container: HTMLElement, ctx: PaneTypeRenderContext, cid: string,
): HTMLElement {
    const group = createFieldGroup({ title: 'Wallets' });
    container.appendChild(group.element);

    group.body.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Your bitcash wallets: what each one holds, the address other people pay it at, '
            + 'and the spending permissions services hold on it. Your wallets are stored in your '
            + "Catalog.ID's encrypted storage, under a key derived from your account's own "
            + 'derivation secret, so you can read and use them on any device you have unlocked.',
    }));

    const body = createElement('div');
    group.body.appendChild(body);

    let loaded: LoadedWallets | null = null;

    /** Draw the group from whatever `loaded` currently holds. */
    const render = (): void => {
        body.innerHTML = '';
        if (!loaded) {
            body.appendChild(createElement('p', {
                className: 'cid-pane-note', textContent: 'Reading your wallets…',
            }));
            return;
        }
        renderLoaded(loaded);
    };

    /**
     * Re-read the document and redraw. Every write goes through the store,
     * which lands its own compare-and-set retry, so this is a plain re-read
     * afterwards rather than a patch of what we believe is there.
     */
    const reload = async (): Promise<void> => {
        try {
            const held = await loadWallets();
            const doc = held?.doc ?? { v: 1 as const, wallets: [] };
            loaded = { doc, slots: walletSlotsFromDocument(doc) };
            render();
        } catch (err) {
            renderUnavailable(err);
        }
    };

    /**
     * One line and a retry button. A locked tab (`cid-locked`) and an account
     * with no derivation secret (`derive-root-missing`) are the two named
     * reasons; anything else is reported as it came. Never an empty box: a
     * member who cannot see their wallets must be told why.
     */
    function renderUnavailable(err: unknown): void {
        loaded = null;
        body.innerHTML = '';
        body.appendChild(createElement('p', {
            className: 'cid-wizard-warning', textContent: walletsUnavailableMessage(err),
        }));
        const row = createElement('div', { className: 'cid-credential-actions' });
        row.appendChild(button('Try again', 'default', () => void reload()));
        body.appendChild(row);
    }

    /** The slot the NEXT wallet takes — REVOKED wallets included, so a slot is
     *  never reused. Recomputed by the store on a CAS retry; this copy only
     *  decides what the *Add wallet…* dialog validates against. */
    const existingRecords = (): BitcashWalletsDocument['wallets'] => loaded?.doc.wallets ?? [];

    function renderLoaded(held: LoadedWallets): void {
        if (held.slots.length === 0) {
            body.appendChild(createElement('p', {
                className: 'cid-pane-note',
                textContent: 'You have no wallet yet. Add one you already have, or mint a new '
                    + 'one — a new wallet is empty until somebody pays into its receive address.',
            }));
        }

        const worker = liveWalletsWorker();
        for (const slot of held.slots) {
            const card = createElement('div', { style: { marginTop: '16px' } });
            body.appendChild(card);
            void renderWalletCard(card, {
                shell: ctx.shell,
                worker,
                slot,
                title: walletDisplayLabel(slot),
                cacheSlot: slot.keyIndex === 0 ? defaultTokenSlot(cid) : null,
                cache: getDefaultTokenCache(),
                canPublish: true,
                reload: () => { void reload(); },
                onRename: async (label) => {
                    await renameWallet(slot.keyIndex, label);
                    await reload();
                },
                onRevoke: async () => {
                    await revokeWallet(slot.keyIndex);
                    await reload();
                },
            });
        }

        const actions = createElement('div', { className: 'cid-credential-actions' });
        actions.appendChild(button('Add wallet…', 'default', () => void showAddWalletModal()));
        actions.appendChild(button('New wallet…', 'default', () => void showNewWalletModal()));
        body.appendChild(actions);
    }

    async function showAddWalletModal(): Promise<void> {
        const values = await promptForm(ctx.shell, {
            title: 'Add a wallet',
            description: 'Paste the 34-character wallet code of a bitcash wallet you already hold. '
                + "It must be a CASH code. The code is stored in your Catalog.ID's "
                + 'encrypted storage.',
            fields: [
                { key: 'code', label: 'Wallet code', placeholder: 'CASH-…' },
                { key: 'label', label: 'Name (optional)', placeholder: 'e.g. Savings' },
            ],
            submitLabel: 'Add wallet',
        });
        if (!values) return;
        try {
            await addWallet(planWalletImport({
                code: values.code,
                existing: existingRecords(),
                label: values.label,
            }));
            await reload();
            ctx.shell.toast('Wallet added', 'success');
        } catch (err) {
            ctx.shell.toast(err instanceof Error ? err.message : 'The wallet could not be added', 'error');
        }
    }

    async function showNewWalletModal(): Promise<void> {
        const values = await promptForm(ctx.shell, {
            title: 'New wallet',
            description: 'Mints a new bitcash wallet key and stores it in your Catalog.ID\'s '
                + 'encrypted storage. The ledger creates the wallet with its first credit of at least '
                + '1000 BIT — a gift redeemed into it, or bits released into it — and only then can it '
                + 'take a receive address.',
            fields: [{ key: 'label', label: 'Name (optional)', placeholder: 'e.g. Project wallet' }],
            submitLabel: 'Create wallet',
        });
        if (!values) return;
        try {
            await addWallet(planNewWallet({ label: values.label }));
            await reload();
            ctx.shell.toast('New wallet created', 'success');
        } catch (err) {
            ctx.shell.toast(err instanceof Error ? err.message : 'The wallet could not be created', 'error');
        }
    }

    render();
    void reload();
    return group.element;
}

// ── the wallet card, shared by both surfaces ────────────────────────────────

export interface WalletCardOptions {
    shell: ShellAPI;
    worker: WalletsWorker;
    slot: WalletSlot;
    title: string;
    /** The device cache slot of THIS wallet's default token, or null when the
     *  plan defines none for it (§4.12). */
    cacheSlot: string | null;
    cache: DefaultTokenCache | null;
    /** Settings only: publication is `session.AccountId`-scoped server-side. */
    canPublish: boolean;
    /** Absent on the organization surface, whose bag this session cannot write. */
    onRename?: (label: string) => Promise<void>;
    /** Settings only: retire this wallet. The record STAYS in the document with
     *  `status: 'revoked'` — the slot is never reused, and the bitcash manager
     *  learns to stop offering the wallet from the status, which it could not
     *  learn from an absence (cid-encrypted-storage §7.2). */
    onRevoke?: () => Promise<void>;
    /** Organization surface only (§4.13). */
    /** `serviceTypes` is the node's configured list: a delegate token is unbound too (§9.3). */
    onMintDelegateToken?: (delegateCid: string, caps: TokenCaps, serviceTypes: readonly string[]) => Promise<void>;
    /** 'organization' when a delegate draws the organization's wallet: its codes are this
     *  delegate's own, and other delegates' instruments show by ref (default 'account'). */
    surface?: WalletSurface;
    reload: () => void;
}

/**
 * Draw one wallet: balance, receive address, tokens. Every Worker call is made
 * here and reported here — a wallet whose Worker is unreachable renders as a
 * wallet with an explanation, never as an empty box.
 */
export async function renderWalletCard(host: HTMLElement, opts: WalletCardOptions): Promise<void> {
    host.innerHTML = '';
    const header = createElement('div', { className: 'cid-share-item' });
    header.appendChild(createElement('span', {
        className: 'cid-share-cid',
        textContent: opts.title,
    }));
    if (opts.onRename) {
        header.appendChild(button('Rename', 'default', () => void rename()));
    }
    if (opts.onRevoke) {
        header.appendChild(button('Retire', 'default', () => void retire()));
    }
    host.appendChild(header);

    const status = createElement('p', { className: 'cid-pane-note', textContent: 'Reading the wallet…' });
    host.appendChild(status);

    let view: WalletView;
    try {
        view = await loadWalletView(opts.worker, opts.slot);
    } catch (err) {
        status.textContent = err instanceof Error ? err.message : 'This wallet could not be read';
        return;
    }
    status.remove();

    if (view.error) {
        host.appendChild(createElement('p', { className: 'cid-wizard-warning', textContent: view.error }));
    }

    if (view.missing) {
        host.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'This wallet is not on the ledger yet. It is created by its first credit of at '
                + 'least 1000 BIT — a gift redeemed into it, or bits released into it — and can take a '
                + 'receive address and spending permissions from then on.',
        }));
        return;
    }

    host.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: view.balance === null
            ? 'Balance unavailable.'
            : `Balance: ${view.balance} BIT.`,
    }));

    // ── receive address ─────────────────────────────────────────────────────
    const addressBox = createElement('div');
    host.appendChild(addressBox);
    renderAddress(addressBox, opts, view);

    // ── tokens ──────────────────────────────────────────────────────────────
    host.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Spending permissions. Each one lets a service debit this wallet within its caps; '
            + 'revoking one takes that permission away immediately. A wallet holds at most 32 live '
            + 'permissions and 128 in total, revoked ones included.',
    }));

    const live = view.tokens.filter(t => isLiveToken(t));
    if (live.length === 0) {
        host.appendChild(createElement('p', {
            className: 'cid-pane-note', textContent: 'No spending permission is live on this wallet.',
        }));
    }
    for (const token of live) {
        host.appendChild(tokenRow(opts, token));
    }

    const actions = createElement('div', { className: 'cid-credential-actions' });
    const hasDefault = findDefaultToken(view.tokens) !== null;
    actions.appendChild(button(
        hasDefault ? 'Reissue default permission' : 'Issue default permission', 'default',
        () => void reissue()));
    if (opts.onMintDelegateToken) {
        actions.appendChild(button('Mint a token for this delegate…', 'default',
            () => void mintForDelegate()));
    }
    host.appendChild(actions);

    // ── actions ─────────────────────────────────────────────────────────────

    async function rename(): Promise<void> {
        const values = await promptForm(opts.shell, {
            title: 'Rename wallet',
            description: "The name is stored with the wallet key in your Catalog.ID's encrypted "
                + 'storage. At most 64 characters.',
            fields: [{ key: 'label', label: 'Name', value: opts.slot.label }],
            submitLabel: 'Rename',
        });
        if (!values) return;
        try {
            await opts.onRename!(values.label);
            opts.shell.toast('Wallet renamed', 'success');
        } catch (err) {
            opts.shell.toast(err instanceof Error ? err.message : 'The wallet could not be renamed', 'error');
        }
    }

    async function retire(): Promise<void> {
        const ok = await opts.shell.confirm({
            title: 'Retire this wallet?',
            message: `"${opts.title}" stops being offered for spending and the bitcash manager `
                + 'will stop using it. The key is kept — a retired wallet is not deleted and its '
                + 'slot is never reused — so anything still in it can be recovered.',
            confirmLabel: 'Retire wallet',
        });
        if (!ok) return;
        try {
            await opts.onRevoke!();
            opts.shell.toast('Wallet retired', 'success');
        } catch (err) {
            opts.shell.toast(err instanceof Error ? err.message : 'The wallet could not be retired', 'error');
        }
    }

    async function reissue(): Promise<void> {
        const caps = await tokenCaps();
        if (caps === null) {
            opts.shell.toast(NO_CAPS_MESSAGE, 'warning');
            return;
        }
        const prompt = defaultPermissionPrompt({ surface: opts.surface ?? 'account', hasDefault, caps });
        const ok = await opts.shell.confirm({
            title: prompt.title,
            message: prompt.message,
            confirmLabel: hasDefault ? 'Reissue' : 'Issue',
            confirmVariant: hasDefault ? 'no' : 'yes',
        });
        if (!ok) return;
        try {
            await reissueDefaultToken({
                worker: opts.worker, walletKey: opts.slot.key,
                caps: { dailyCap: caps.dailyCap, monthlyCap: caps.monthlyCap }, serviceTypes: caps.serviceTypes,
                cacheSlot: opts.cacheSlot, cache: opts.cache,
            });
            opts.shell.toast('Default spending permission issued', 'success');
            opts.reload();
        } catch (err) {
            opts.shell.toast(err instanceof Error ? err.message : 'The permission could not be issued', 'error');
        }
    }

    async function mintForDelegate(): Promise<void> {
        const defaults = await tokenCaps();
        // Unbound like the default token, so scoped to the node's service types (§9.3): none, no token.
        if (defaults === null) {
            opts.shell.toast(NO_CAPS_MESSAGE, 'warning');
            return;
        }
        const values = await promptForm(opts.shell, {
            title: 'Mint a token for a delegate',
            description: 'A spending permission on the organization\'s wallet for ONE delegate, so '
                + 'their work is attributable on the ledger. It takes one of the wallet\'s 32 live '
                + 'permission slots (128 including revoked ones), which is why it is issued '
                + 'deliberately rather than handed out automatically.',
            fields: [
                { key: 'delegateCid', label: 'Delegate CID', placeholder: 'e.g. ab12cd34fg' },
                { key: 'dailyCap', label: 'Daily cap (BIT)', value: String(defaults.dailyCap) },
                { key: 'monthlyCap', label: 'Monthly cap (BIT)', value: String(defaults.monthlyCap) },
            ],
            submitLabel: 'Mint token',
        });
        if (!values) return;
        try {
            const caps = requireCaps({
                dailyCap: Number(values.dailyCap), monthlyCap: Number(values.monthlyCap),
            });
            await opts.onMintDelegateToken!(values.delegateCid.trim(), caps, defaults.serviceTypes);
            opts.shell.toast(`Token ${delegateTokenLabel(values.delegateCid.trim())} minted`, 'success');
            opts.reload();
        } catch (err) {
            opts.shell.toast(err instanceof Error ? err.message : 'The token could not be minted', 'error');
        }
    }

    function tokenRow(o: WalletCardOptions, token: WalletToken): HTMLElement {
        const row = createElement('div', { className: 'cid-share-item' });
        const isDefault = token.label === DEFAULT_TOKEN_LABEL;
        const parts = [
            tokenOwnerText(token, o.surface ?? 'account'),
            tokenPayeeText(token),
            token.sites.length ? `sites: ${token.sites.join(', ')}` : 'any site',
            token.serviceTypes.length ? `services: ${token.serviceTypes.join(', ')}` : 'any service',
            `caps ${token.dailyCap?.toLocaleString() ?? 'none'}/day · ${token.monthlyCap?.toLocaleString() ?? 'none'}/month (BIT)`,
            token.expiresAt ? `expires ${new Date(token.expiresAt).toLocaleDateString()}` : 'no expiry',
        ];
        // Only when the Worker's counters actually came across: an absent field
        // means "not reported", which is not the same as "nothing spent".
        if (typeof token.daySpent === 'number' || typeof token.monthSpent === 'number') {
            parts.push(`spent ${(token.daySpent ?? 0).toLocaleString()} today · `
                + `${(token.monthSpent ?? 0).toLocaleString()} this month`);
        }
        row.appendChild(createElement('span', { className: 'cid-share-cid', textContent: parts.join(' · ') }));
        row.appendChild(button('Revoke', 'no', async () => {
            const ok = await o.shell.confirm({
                title: 'Revoke permission',
                message: isDefault
                    ? 'The services that hold this permission will ask you to approve a new one the '
                      + 'next time they need to pay. Revoke it?'
                    : `Revoke the permission labelled "${token.label ?? 'unlabelled'}"? The service `
                      + 'holding it can no longer debit this wallet.',
                confirmLabel: 'Revoke',
                confirmVariant: 'no',
            });
            if (!ok) return;
            try {
                await revokeWalletToken({
                    worker: o.worker, walletKey: o.slot.key, token,
                    cacheSlot: o.cacheSlot, cache: o.cache,
                });
                o.shell.toast('Permission revoked', 'success');
                o.reload();
            } catch (err) {
                o.shell.toast(err instanceof Error ? err.message : 'The permission could not be revoked', 'error');
            }
        }));
        return row;
    }
}

function renderAddress(host: HTMLElement, opts: WalletCardOptions, view: WalletView): void {
    host.innerHTML = '';
    const address = view.receiveAddress;
    if (!address && view.receiveAddressHeldElsewhere) {
        // The organization's receive address is kept by the delegate who created it (the
        // founder): a receive address is publishable, not secret, but its code is not here,
        // so nothing on this card may need it — and a second address would cost a fee.
        host.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'This wallet has a receive address, but its code is kept by the delegate who created it '
                + '(the organization\'s founding delegate), not by you. Ask them for it when someone needs to pay '
                + 'the organization.',
        }));
        return;
    }
    if (!address) {
        host.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'This wallet has no receive address yet. The first one is free; it is the '
                + 'code other people use to pay this wallet.',
        }));
        const row = createElement('div', { className: 'cid-credential-actions' });
        row.appendChild(button('Create receive address', 'yes', async () => {
            try {
                const created = await ensureReceiveAddress(opts.worker, opts.slot.key);
                opts.shell.toast(`Receive address ${created} created`, 'success');
                opts.reload();
            } catch (err) {
                opts.shell.toast(
                    err instanceof Error ? err.message : 'The address could not be created', 'error');
            }
        }));
        host.appendChild(row);
        return;
    }

    const display = displayWalletKey(address) ?? address;
    const row = createElement('div', { className: 'cid-share-item' });
    row.appendChild(createElement('span', {
        className: 'cid-share-cid', textContent: `Receive address: ${display}`,
    }));
    row.appendChild(button('Copy', 'default', async () => {
        try {
            await navigator.clipboard.writeText(display);
            opts.shell.toast('Receive address copied', 'success');
        } catch {
            opts.shell.toast('Could not copy — select the address and copy it by hand', 'warning');
        }
    }));
    host.appendChild(row);

    const state = createElement('p', { className: 'cid-pane-note', textContent: '' });
    host.appendChild(state);

    if (!opts.canPublish) {
        state.textContent = 'The organization\'s receive address cannot be published yet: a '
            + 'publication belongs to the account that owns it, and an organization account does '
            + 'not sign in.';
        return;
    }

    void (async () => {
        let existing = null;
        try {
            existing = findReceiveAddressPublication(await publicClaimsApi.listPublicClaims(), display);
        } catch { /* the publish button still works */ }

        state.textContent = existing
            ? `Published as a public claim — ${existing.status}`
              + (existing.rejectionReason ? ` (${existing.rejectionReason})` : '') + '.'
            : 'Not published. Publishing it lets anyone who looks up this identity pay this wallet; '
              + 'it is optional and off by default.';

        const row2 = createElement('div', { className: 'cid-credential-actions' });
        row2.appendChild(button(existing ? 'Publish again' : 'Publish', 'default', async () => {
            try {
                await publishFromSession(display);
                opts.shell.toast('Receive address submitted for publication', 'success');
                opts.reload();
            } catch (err) {
                opts.shell.toast(
                    formatApiError(err, 'The address could not be published'), 'error');
            }
        }));
        host.appendChild(row2);
    })();
}

/**
 * Publish THIS session's receive address. The key material is the session's own
 * `attr_encrypt` pair — the same pair every other self-asserted claim is
 * created under — and the claim key is re-wrapped to each operator so the
 * publication can be reviewed (the server refuses a publication with no
 * operator keys whenever an operator exists).
 */
export async function publishFromSession(address: string): Promise<{ claimUuid: string }> {
    const secret = keyState.getPrivateKey('attr_encrypt');
    if (!secret) throw new Error('Your keys are locked — unlock them and try again');
    const publicKeys = await accountApi.getOwnPublicKeys();
    const attrPub = publicKeys
        .filter(k => k.keyId === 'attr_encrypt')
        .sort((a, b) => b.keyIndex - a.keyIndex)[0];
    if (!attrPub) throw new Error('No attr_encrypt public key found for this account');

    const chain = keyState.getKeyChain('attr_encrypt');
    return await publishReceiveAddressClaim({
        encryptClaim,
        createClaim: claimsApi.createClaim,
        listOperators: publicClaimsApi.listOperators,
        publishClaim: publicClaimsApi.publishClaim,
        // A rotated account's claim key is wrapped to the NEWEST generation, so
        // the re-wrap walks the chain exactly as the claim pane's publish does.
        reEncryptClaimKey: async (encryptedKey, sharerSecret, recipientPublic) =>
            attrPub.keyIndex > 0
                ? await reEncryptClaimKeyWithChain(encryptedKey, chain, attrPub.keyIndex, recipientPublic)
                : await reEncryptClaimKey(encryptedKey, sharerSecret, recipientPublic),
        fromBase64: aes.fromBase64,
        ownerPublicKey: aes.fromBase64(attrPub.publicKey),
        ownerSecretKey: aes.fromBase64(secret),
    }, { address });
}

// ── small DOM helpers ───────────────────────────────────────────────────────

function button(
    label: string, variant: 'yes' | 'no' | 'default', onClick: () => void | Promise<void>,
): HTMLElement {
    const el = createElement('button', {
        className: `cs-btn cs-btn--${variant} cs-btn--small`,
        textContent: label,
    });
    el.addEventListener('click', () => { void onClick(); });
    return el;
}

interface PromptField {
    key: string;
    label: string;
    value?: string;
    placeholder?: string;
}

/**
 * A one-shot modal form. Resolves with the trimmed values, or null when the
 * member cancelled — the same contract `promptAndDeriveKey` uses, so a caller
 * never has to tell "cancelled" from "empty".
 */
function promptForm(shell: ShellAPI, config: {
    title: string; description: string; fields: PromptField[]; submitLabel: string;
}): Promise<Record<string, string> | null> {
    return new Promise((resolve) => {
        let done = false;
        const finish = (value: Record<string, string> | null): void => {
            if (done) return;
            done = true;
            handle.close();
            resolve(value);
        };

        const card = createElement('div', { className: 'cid-wizard-step' });
        card.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: config.title }));
        card.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: config.description }));

        const inputs = new Map<string, HTMLInputElement>();
        for (const field of config.fields) {
            const wrap = createElement('div', { className: 'cid-auth-field' });
            wrap.appendChild(createElement('label', { className: 'cid-wizard-text', textContent: field.label }));
            const input = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
            input.type = 'text';
            input.value = field.value ?? '';
            if (field.placeholder) input.placeholder = field.placeholder;
            wrap.appendChild(input);
            card.appendChild(wrap);
            inputs.set(field.key, input);
        }

        const footer = createElement('div', { className: 'cid-credential-actions' });
        footer.appendChild(button('Cancel', 'default', () => finish(null)));
        footer.appendChild(button(config.submitLabel, 'yes', () => {
            const values: Record<string, string> = {};
            for (const [key, input] of inputs) values[key] = input.value.trim();
            finish(values);
        }));
        card.appendChild(footer);

        const handle = shell.showModal(card, { size: 'normal', onClose: () => finish(null) });
        inputs.values().next().value?.focus();
    });
}
